import { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode, RefObject } from 'react';
import storage from 'local-storage-fallback';

import { SidebarProvider, SidebarInset, SidebarTrigger } from './ui/sidebar';
import { EhSidebar } from './EhSidebar';
import { EhHeader } from './EhHeader';
import EhCallManager from './EhCallManager';
import useScrollTopOnNavigation from '@/hooks/useScrollTopOnNavigation';
import { EhOfflineDialog } from './EhOfflineDialog';
import { LS_OFFLINE_REFRESH_TSTMP_KEY } from '@/lib/constants';

interface EhLayoutProps {
  children: ReactNode;
  showSidebarTrigger?: boolean;
}

const EhLayoutContext = createContext<{
  headerRef: RefObject<HTMLElement | null> | null
}>({ headerRef: null });

export function useEhLayout() {
  return useContext(EhLayoutContext);
}

export function EhLayout({ children, showSidebarTrigger = true }: EhLayoutProps) {
  const [offlineDialogOpen, setOfflineDialogOpen] = useState(false);
  const [hasRecentlyRefreshed, setHasRecentlyRefreshed] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);

  useScrollTopOnNavigation();

  useEffect(() => {
    const hasRecentlyRefreshed = () => {
      const threeHours = 1000 * 60 * 60 * 3;
      const offlineRefreshTimestamp = storage.getItem(LS_OFFLINE_REFRESH_TSTMP_KEY);
      return offlineRefreshTimestamp && Date.now() - parseInt(offlineRefreshTimestamp) < threeHours;
    };

    if (hasRecentlyRefreshed()) setHasRecentlyRefreshed(true);

    const OFFLINE_CHANGE_DELAY = 3000; // to avoid false positives
    const handleOffline = () => {
      setTimeout(() => {
        if (!navigator.onLine) setOfflineDialogOpen(true);
      }, OFFLINE_CHANGE_DELAY);
    };

    const handleOnline = () => {
      setTimeout(() => {
        if (navigator.onLine && !hasRecentlyRefreshed()) {
          // auto-refresh only once after offline
          storage.setItem(LS_OFFLINE_REFRESH_TSTMP_KEY, `${Date.now()}`);
          window.location.reload();
        } else if (navigator.onLine) {
          setOfflineDialogOpen(false);
        }
      }, OFFLINE_CHANGE_DELAY);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return (
    <SidebarProvider defaultOpen={false}>
      <EhLayoutContext.Provider value={{ headerRef }}>
        <EhSidebar />
        <SidebarInset className="overflow-hidden">
          <div className="min-h-dvh bg-zinc-900 text-white flex flex-col">
            <EhHeader ref={headerRef} SidebarTrigger={showSidebarTrigger && (
              <SidebarTrigger className="md:hidden text-white bg-zinc-800/80 backdrop-blur-sm hover:bg-zinc-700 p-2 rounded-lg" />
            )} />
            {children}
          </div>
          <EhCallManager />
          <EhOfflineDialog open={offlineDialogOpen} onOpenChange={setOfflineDialogOpen} alreadyRefreshed={hasRecentlyRefreshed} />
        </SidebarInset>
      </EhLayoutContext.Provider>
    </SidebarProvider>
  );
}
